import React, { useCallback, useRef, useState } from "react";
import "./HoloCard.css";

const clamp = (v, lo = 0, hi = 100) => Math.min(hi, Math.max(lo, v));

/**
 * A standalone holographic card for the landing showcase.
 * Mouse tracking drives CSS custom properties for the
 * shine / glare overlays.
 */
export default function HoloCard({
  label = "",
  sublabel = "",
  gradient = "linear-gradient(135deg, #281c59, #1d192c)",
  className = "",
}) {
  const ref = useRef(null);
  const [vars, setVars] = useState({
    "--hc-px": "50%",
    "--hc-py": "50%",
    "--hc-bx": "50%",
    "--hc-by": "50%",
    "--hc-rx": "0deg",
    "--hc-ry": "0deg",
    "--hc-o": "0",
  });

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const ax = e.clientX - r.left;
    const ay = e.clientY - r.top;
    const px = clamp((ax / r.width) * 100);
    const py = clamp((ay / r.height) * 100);
    const cx = px - 50;
    const cy = py - 50;
    setVars({
      "--hc-px": `${px}%`,
      "--hc-py": `${py}%`,
      "--hc-bx": `${clamp(37 + (px / 100) * 26)}%`,
      "--hc-by": `${clamp(33 + (py / 100) * 34)}%`,
      "--hc-rx": `${(-(cx / 4)).toFixed(1)}deg`,
      "--hc-ry": `${((cy / 4)).toFixed(1)}deg`,
      "--hc-o": "1",
    });
  }, []);

  const onDown = useCallback((e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onReset = useCallback(() => {
    setVars({
      "--hc-px": "50%",
      "--hc-py": "50%",
      "--hc-bx": "50%",
      "--hc-by": "50%",
      "--hc-rx": "0deg",
      "--hc-ry": "0deg",
      "--hc-o": "0",
    });
  }, []);

  return (
    <div
      ref={ref}
      className={`holo-card ${className}`}
      style={vars}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onReset}
      onPointerLeave={onReset}
      onPointerCancel={onReset}
    >
      <div className="holo-card__rotator">
        {/* Card face */}
        <div className="holo-card__face" style={{ background: gradient }}>
          {/* Decorative inner elements */}
          <div className="holo-card__dots" />
          {label && <span className="holo-card__label">{label}</span>}
          {sublabel && <span className="holo-card__sublabel">{sublabel}</span>}
        </div>
        {/* Shine overlay */}
        <div className="holo-card__shine" />
        {/* Glare overlay */}
        <div className="holo-card__glare" />
      </div>
    </div>
  );
}
